import type { Maybe } from '@takeshape/util';
import { getUserId } from 'app/auth/selectors';
import { createRoutine } from 'app/common/utils/new-routines';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import type { ProjectSchemaCompat } from 'app/compat/types';
import { compatSchemaToSchema } from 'app/compat/utils';
import { showMessage } from 'app/messages/actions';
import { getCurrentProjectId } from 'app/project/selectors';
import { SITE_SEARCH_NAME } from 'app/sites/constants';
import type { SagaIterator } from 'redux-saga';
import { all, call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { initContentSearch } from '../../content-search/actions.js';
import { setProjectEndpoint, type WrappedGraphQLError } from '../../graphql-client.js';
import { setProjectSchema, setUserSchema } from '../actions.js';
import { updateOrCreateSchemaMutation } from '../api.js';
import { DEFAULT_BRANCH, NAME, SCRATCH_BRANCH } from '../constants.js';
import { useWorkingCopyStore } from '../store/working-copy-store.js';
import { branchToBranchQueryArgs, getSchemaRecordType, summarizeApiError } from '../utils.js';
import { checkBranchStatus } from './check-branch-status.js';
import { fetchServiceLayersRoutine } from './fetch-service-layers.js';
export type PostSchemaRoutineMeta = {
  delay?: number;
  branch?: string;
  runtime?: boolean;
};

export type PostSchemaRoutineFulfilledPayload = {
  userSchema: ProjectSchemaCompat;
  runtimeSchema: ProjectSchemaCompat;
};

export const postSchemaRoutine = createRoutine(`${NAME}/POST_SCHEMA`)<
  PostSchemaRoutineMeta,
  ProjectSchemaCompat,
  undefined,
  PostSchemaRoutineFulfilledPayload,
  WrappedGraphQLError,
  undefined
>();

export function* postSchemaRequestedSaga(
  payload: ProjectSchemaCompat,
  { branch = DEFAULT_BRANCH, runtime, delay: delayMs }: PostSchemaRoutineMeta
): SagaIterator {
  const projectId: Maybe<string> = yield select(getCurrentProjectId);

  if (!projectId) {
    return;
  }

  const userId: Maybe<string> = yield select(getUserId);

  if (delayMs) {
    yield delay(delayMs);
  }

  const { version } = payload;
  const schema = compatSchemaToSchema(payload);
  const branchQueryArgs = branchToBranchQueryArgs(projectId, branch, userId);
  const schemaType = getSchemaRecordType(runtime ?? true);

  const runtimeSchema = yield call(updateOrCreateSchemaMutation, {
    ...branchQueryArgs,
    schemaType,
    version,
    schema
  });

  setProjectEndpoint(projectId, branch, runtimeSchema.schemaHash);
  const userSchema = {
    ...payload,
    version: runtimeSchema.version
  };

  return {
    userSchema,
    runtimeSchema
  };
}

export function* postSchemaFulfilledSaga({
  payload,
  meta: { branch = DEFAULT_BRANCH }
}: ActionType<typeof postSchemaRoutine.fulfilled>): SagaIterator {
  if (branch === SCRATCH_BRANCH) {
    yield put(setUserSchema({ branch: SCRATCH_BRANCH, schema: payload.userSchema }));
    yield put(
      setProjectSchema({
        branch: SCRATCH_BRANCH,
        schema: payload.runtimeSchema
      })
    );
  } else {
    yield call(useWorkingCopyStore.getState().reset);
    yield put(showMessage('info', 'Schema deployed', 1500));
    yield put(checkBranchStatus.requested());
    yield put(
      initContentSearch({
        searchName: SITE_SEARCH_NAME
      })()
    );
  }

  yield put(fetchServiceLayersRoutine.requested(undefined, { branch }));
}

export function* postSchemaRejectedSaga({
  payload,
  meta
}: ActionType<typeof postSchemaRoutine.rejected>): SagaIterator {
  const { branch } = meta;
  const prefix = branch === SCRATCH_BRANCH ? 'Error previewing work in progress' : 'Could not deploy schema';
  let message = summarizeApiError(payload.message, prefix);

  if (payload.rawError[0].extensions?.validationErrors) {
    message += `\n${summarizeApiError(payload.rawError[0].extensions?.validationErrors)}`;
  }

  console.trace('Post schema rejected', message);
  yield put(showMessage('error', message));
}

export default function* root(): SagaIterator {
  yield all([
    takeEvery(
      postSchemaRoutine.requested,
      getRoutineSaga(postSchemaRoutine, postSchemaRequestedSaga, {
        handleBasicErrors: true,
        rejectWithFullError: true
      })
    ),
    takeLatest(postSchemaRoutine.fulfilled, postSchemaFulfilledSaga),
    takeLatest(postSchemaRoutine.rejected, postSchemaRejectedSaga)
  ]);
}
